import React, { useState, useEffect } from 'react';
import { X, Loader2, Clock, CheckCircle, AlertCircle, Sparkles, ShoppingBag, Trash2 } from 'lucide-react';
import { stylistAPI } from '../../../../services/api';

const statusConfig = {
  new: { label: 'Новый', color: '#f59e0b', icon: AlertCircle, next: 'in_progress', nextLabel: 'Взять в работу' },
  in_progress: { label: 'В работе', color: '#3b82f6', icon: Clock, next: 'completed', nextLabel: 'Отметить выполненным' },
  completed: { label: 'Выполнен', color: '#22c55e', icon: CheckCircle, next: null, nextLabel: null }
};

const typeConfig = {
  look: { label: 'Подбор образа', icon: Sparkles },
  shopping: { label: 'Рекомендация к покупке', icon: ShoppingBag }
};

const RequestDetailModal = ({ requestId, onClose, onUpdated, onDeleted }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const data = await stylistAPI.getRequestDetail(requestId);
      setRequest(data);
    } catch (err) {
      setError('Ошибка загрузки запроса');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    const status = statusConfig[request.status];
    if (!status.next) return;

    setUpdating(true);
    try {
      await stylistAPI.updateRequestStatus(requestId, status.next);
      setRequest({ ...request, status: status.next });
      onUpdated();
    } catch (err) {
      setError('Ошибка обновления статуса');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить этот запрос?')) return;

    setUpdating(true);
    try {
      await stylistAPI.deleteRequest(requestId);
      onDeleted(requestId);
      onClose();
    } catch (err) {
      setError('Ошибка удаления');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        <div style={{ backgroundColor: 'var(--white)', padding: 'var(--space-xxl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const status = statusConfig[request.status] || statusConfig.new;
  const type = typeConfig[request.request_type] || typeConfig.look;
  const StatusIcon = status.icon;
  const TypeIcon = type.icon;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xxl)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-xl)'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-m)',
              marginBottom: 'var(--space-s)'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: 'var(--cream)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem'
              }}>
                <TypeIcon size={14} />
                {type.label}
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem'
              }}>
                <StatusIcon size={14} />
                {status.label}
              </span>
            </div>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              color: 'var(--light-gray)'
            }}>
              Создан {request.created_by === 'stylist' ? 'стилистом' : 'клиентом'} • {new Date(request.created_at).toLocaleString('ru-RU')}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <h4 style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'var(--mid-gray)',
            marginBottom: 'var(--space-s)'
          }}>
            Описание
          </h4>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap'
          }}>
            {request.description}
          </p>
        </div>

        {/* Budget & Deadline */}
        {(request.budget || request.deadline) && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-xxl)',
            marginBottom: 'var(--space-xl)',
            padding: 'var(--space-m)',
            backgroundColor: 'var(--cream)'
          }}>
            {request.budget && (
              <div>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  color: 'var(--light-gray)',
                  textTransform: 'uppercase'
                }}>
                  Бюджет
                </span>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  marginTop: '4px'
                }}>
                  {request.budget}
                </p>
              </div>
            )}
            {request.deadline && (
              <div>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  color: 'var(--light-gray)',
                  textTransform: 'uppercase'
                }}>
                  Дедлайн
                </span>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  marginTop: '4px'
                }}>
                  {new Date(request.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Photos */}
        {request.photos && request.photos.length > 0 && (
          <div style={{ marginBottom: 'var(--space-xl)' }}>
            <h4 style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              Фото ({request.photos.length})
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 'var(--space-s)'
            }}>
              {request.photos.map((photo, idx) => (
                <a
                  key={idx}
                  href={photo}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={photo}
                    alt=""
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      objectFit: 'cover'
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Linked Items */}
        {(request.linked_looks?.length > 0 || request.linked_recommendations?.length > 0) && (
          <div style={{ marginBottom: 'var(--space-xl)' }}>
            <h4 style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-m)'
            }}>
              Привязанные результаты
            </h4>
            
            {request.linked_looks?.length > 0 && (
              <div style={{ marginBottom: 'var(--space-m)' }}>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  marginBottom: 'var(--space-s)'
                }}>
                  Образы:
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {request.linked_looks.map((look) => (
                    <div
                      key={look.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-s)',
                        padding: '8px 12px',
                        backgroundColor: 'var(--cream)',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.8125rem'
                      }}
                    >
                      {look.look_images?.[0] && (
                        <img
                          src={look.look_images[0]}
                          alt=""
                          style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                        />
                      )}
                      {look.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {request.linked_recommendations?.length > 0 && (
              <div>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  marginBottom: 'var(--space-s)'
                }}>
                  Рекомендации к покупке:
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {request.linked_recommendations.map((rec) => (
                    <div
                      key={rec.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-s)',
                        padding: '8px 12px',
                        backgroundColor: 'var(--cream)',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.8125rem'
                      }}
                    >
                      {rec.image_url && (
                        <img
                          src={rec.image_url}
                          alt=""
                          style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                        />
                      )}
                      {rec.description.substring(0, 30)}...
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <p style={{
            color: '#ef4444',
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            marginBottom: 'var(--space-m)'
          }}>
            {error}
          </p>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-m)',
          borderTop: '1px solid var(--soft-gray)',
          paddingTop: 'var(--space-l)'
        }}>
          {status.next && (
            <button
              onClick={handleStatusChange}
              disabled={updating}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: status.next === 'completed' ? '#22c55e' : 'var(--black)',
                color: 'var(--white)',
                border: 'none',
                fontFamily: 'var(--font-body)',
                cursor: updating ? 'wait' : 'pointer',
                opacity: updating ? 0.5 : 1
              }}
            >
              {updating ? 'Обновление...' : status.nextLabel}
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={updating}
            style={{
              padding: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #ef4444',
              color: '#ef4444',
              fontFamily: 'var(--font-body)',
              cursor: updating ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-s)'
            }}
          >
            <Trash2 size={16} />
            Удалить
          </button>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default RequestDetailModal;
